import {
  isOeReviewer,
  isInternalReviewer,
  isExternalReviewer,
  isQuestDesigner,
  isQuestCopyEditor,
  isQuestManager,
  isQuestCopyEditorOrQuestAuthorOrQuestManager
} from './user'

import {
  isBasicInformationComplete,
  areActivitiesComplete,
  areImagesComplete,
  isOtherInformationComplete
} from './quest'

import {
  experience,
  subject,
  copy,
  standards,
  final,
  needsEdits,
  draft,
  approved,
  internalReviewInProgress,
  inQualityReview,
  published,
  reviewReturned
} from './messages'
import { messages } from './messages/label'

export function userCanViewInternalReviewInfo(user, quest) {
  return (
    !isExternalReviewer(user) &&
    (isQuestDesigner(user) ||
      isQuestCopyEditor(user) ||
      isInternalReviewer(user) ||
      isQuestManager(user) ||
      (isOeReviewer(user) && questIsInQualityReview(quest)) ||
      questIsPublished(quest))
  )
}

export function getReviewStatus(quest) {
  return quest.reviewing_compiled_quest
    ? quest.reviewing_compiled_quest.state
    : 'draft'
}

export function getFriendlyStatus(reviewStatus) {
  var retVal = ''
  switch (reviewStatus) {
    case 'pre-published':
      retVal = 'Pre-Published'
      break
    case 'internal_review':
      retVal = internalReviewInProgress
      break
    case 'submitted':
      retVal = inQualityReview
      break
    case 'draft':
      retVal = draft
      break
    case 'platform_review':
      retVal = inQualityReview
      break
    case 'in-quality-review':
      retVal = inQualityReview
      break
    case 'in-publication-review':
      retVal = inQualityReview
      break
    case 'publication-review':
      retVal = inQualityReview
      break
    case 'published':
      retVal = published
      break
    case 'rejected':
      retVal = reviewReturned
      break
    case 'review-returned':
      retVal = reviewReturned
      break
    case 'recalled':
      retVal = draft
      break
    case 'all-quests':
      retVal = 'All quests'
      break
  }
  return retVal
}

export function allRequiredFieldsComplete(quest, activities) {
  return (
    isBasicInformationComplete(quest) &&
    areActivitiesComplete(activities) &&
    areImagesComplete(quest) &&
    isOtherInformationComplete(quest)
  )
}

export function canSubmitForReview(user, quest, organization, activities) {
  return (
    isQuestCopyEditorOrQuestAuthorOrQuestManager(user, quest, organization) &&
    allRequiredFieldsComplete(quest, activities) &&
    questIsInDraftState(quest)
  )
}

export function canSubmitButNotReady(user, quest, organization, activities) {
  return (
    isQuestCopyEditorOrQuestAuthorOrQuestManager(user, quest, organization) &&
    !allRequiredFieldsComplete(quest, activities) &&
    questIsInDraftState(quest)
  )
}

export function isSomeReviewComplete(reviews) {
  return reviews.some(function (review) {
    return review.accepted === true || review.accepted === false
  })
}

export function getTotalReviewState(reviews) {
  const filteredReviews = reviews.filter(function (review) {
    return review.criteria === 'standards' || review.criteria === 'final'
  })

  var approvedCount = 0
  var rejectedCount = 0
  var reviewState = ''

  filteredReviews.forEach(function (review) {
    if (review.accepted === true) {
      approvedCount += 1
    } else if (review.accepted === false) {
      rejectedCount += 1
    }
  })

  if (rejectedCount > 0) {
    reviewState = 'rejected'
  } else if (approvedCount === 1) {
    reviewState = 'someOeApproved'
  } else if (approvedCount === 2) {
    reviewState = 'readyForPublish'
  }

  return reviewState
}

export function translateReviewState(reviewState) {
  var retVal = ''
  switch (reviewState) {
    case 'rejected':
      retVal = 'rejected'
      break
    case 'allInternalApproved':
      retVal = 'platform_review'
      break
    case 'someOeApproved':
      retVal = 'platform_review'
      break
    case 'readyForPublish':
      retVal = 'published'
      break
  }

  return retVal
}

export function findReview(reviews, reviewType) {
  return reviews.find(function (r) {
    return r.criteria === reviewType
  })
}

export function findRejectedReview(reviews) {
  var rejectedReview = reviews.find(function (review) {
    return review.accepted === false
  })
  return rejectedReview ? rejectedReview.criteria : ''
}

export function getFilteredReviews(reviews, reviewType) {
  var filteredReviews = reviews.filter(function (review) {
    return review.criteria === reviewType
  })
  return filteredReviews
}

export function getCompletedReviewInfo(review) {
  return review.accepted === true ? approved : needsEdits
}

export function getReviewApprovalStatus(review) {
  var approvalStatus = ''

  switch (review.accepted) {
    case true:
      approvalStatus = 'approved'
      break
    case false:
      approvalStatus = 'rejected'
      break
    default:
      approvalStatus = ''
  }
  return approvalStatus
}

export function checkPreviousReviewsNoCurrent(previousFilteredReviews, review) {
  return previousFilteredReviews.length > 0 && !review
}

export function anyOeReviewsComplete(currentReviews, previousReviews) {
  const mergedReviews = currentReviews.concat(previousReviews)
  return (
    mergedReviews.filter(function (review) {
      return (
        (review.criteria === 'standards' || review.criteria === 'final') &&
        review.completed_at
      )
    }).length > 0
  )
}

export function isActiveWithThisReviewer(activeReview, user) {
  return activeReview && user.id === activeReview.reviewer.id
}

export function questIsInDraftState(quest) {
  return (
    !quest.reviewing_compiled_quest ||
    quest.reviewing_compiled_quest.state === 'recalled'
  )
}

export function getIntlReviewType(reviewType) {
  var intlReviewType
  switch (reviewType) {
    case 'experience':
      intlReviewType = experience
      break
    case 'subject':
      intlReviewType = subject
      break
    case 'copy':
      intlReviewType = copy
      break
    case 'standards':
      intlReviewType = standards
      break
    case 'final':
      intlReviewType = final
      break
  }
  return intlReviewType
}

export function getIntlReviewTypeWithContext(reviewType, intl, context) {
  if (context === 'sentence') {
    return getIntlReviewTypeWithContextInSentence(reviewType, intl)
  } else {
    var intlReviewType
    switch (reviewType) {
      case 'experience':
        intlReviewType = intl.formatMessage(messages.experience)
        break
      case 'subject':
        intlReviewType = intl.formatMessage(messages.subject)
        break
      case 'copy':
        intlReviewType = intl.formatMessage(messages.copy)
        break
      case 'standards':
        intlReviewType = intl.formatMessage(messages.standards)
        break
      case 'final':
        intlReviewType = intl.formatMessage(messages.final)
        break
    }
    return intlReviewType
  }
}

export function getIntlReviewTypeWithContextInSentence(reviewType, intl) {
  var intlReviewType
  switch (reviewType) {
    case 'experience':
      intlReviewType = intl.formatMessage(messages.experienceReview)
      break
    case 'subject':
      intlReviewType = intl.formatMessage(messages.subjectReview)
      break
    case 'copy':
      intlReviewType = intl.formatMessage(messages.copy)
      break
    case 'standards':
      intlReviewType = intl.formatMessage(messages.standardsReview)
      break
    case 'final':
      intlReviewType = intl.formatMessage(messages.final)
      break
  }
  return intlReviewType
}

export function questIsInQualityReview(quest) {
  const status = getReviewStatus(quest)
  if (status === 'submitted' || status === 'platform_review') {
    return true
  } else {
    return false
  }
}

export function questIsInRejectedState(quest) {
  return (
    quest.current_compiled_quest &&
    quest.current_compiled_quest.state === 'rejected' &&
    Boolean(quest.reviewing_compiled_quest)
  )
}

export function questInDraftAfterRejection(quest) {
  return (
    quest.current_compiled_quest &&
    quest.current_compiled_quest.state === 'rejected' &&
    !quest.reviewing_compiled_quest
  )
}

export function questIsPublished(quest) {
  return (
    Boolean(quest.reviewing_compiled_quest) &&
    quest.reviewing_compiled_quest.state === 'published'
  )
}

export function questInDraftAfterPublished(quest) {
  return (
    Boolean(quest.current_compiled_quest) &&
    quest.current_compiled_quest.state === 'published' &&
    !quest.reviewing_compiled_quest
  )
}



// WEBPACK FOOTER //
// ./src/utils/review.js